import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Plus, Play, Sparkles, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Empty, Guide, Loader, Pill } from "@/components/Bits";
import { GradeBox } from "@/components/GradeBox";

const statusTone = { drafting: "blue", draft: "blue", active: "green", superseded: "slate", legacy: "slate", failed: "red" };
const DEPTHS = [
  ["exam", "Exam-focused", "Drill what gets tested"],
  ["balanced", "Balanced", "Cover the essentials, explore a little"],
  ["exploratory", "Exploratory", "Follow curiosity, less drilling"],
];
const field = "w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition";

export default function Curriculum() {
  const [list, setList] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ duration_months: 3, depth: "balanced", focus_note: "" });
  const [open, setOpen] = useState({});
  const [draftInput, setDraftInput] = useState({});
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [grade, setGrade] = useState(null);

  const loadProfile = useCallback(async () => {
    const { data } = await api.get("/profile");
    setProfile(data);
    setGrade(data.grade ?? "");
  }, []);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/curriculum");
    setList(data);
    setSelectedId((prev) => (data.curricula.some((c) => c.curriculum_id === prev) ? prev : data.curricula[0]?.curriculum_id || null));
    return data;
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return setDetail(null);
    const { data } = await api.get(`/curriculum/${id}/tree`);
    setDetail(data);
  }, []);

  useEffect(() => {
    loadList();
    loadProfile();
  }, [loadList, loadProfile]);

  const drafting = (list?.curricula || []).some((c) => c.status === "drafting");

  useEffect(() => {
    if (!drafting) return;
    const id = setInterval(() => loadList(), 6000);
    return () => clearInterval(id);
  }, [drafting, loadList]);

  useEffect(() => {
    const current = (list?.curricula || []).find((c) => c.curriculum_id === selectedId);
    if (!current || current.status === "drafting") return setDetail(null);
    loadDetail(selectedId);
  }, [selectedId, list, loadDetail]);

  const selected = (list?.curricula || []).find((c) => c.curriculum_id === selectedId);
  const editable = selected && ["draft", "active", "superseded"].includes(selected.status);

  const afterEdit = async (data) => {
    if (data.message) (data.moved ? toast.info : toast.success)(data.message, { duration: 6000 });
    if (data.forked) toast.info("Your live plan keeps running — these edits went into a new draft version.", { duration: 7000 });
    const fresh = await loadList();
    const target = data.curriculum_id || selectedId;
    setSelectedId(target);
    if (fresh.curricula.some((c) => c.curriculum_id === target)) await loadDetail(target);
  };

  const run = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      await afterEdit(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const generate = () =>
    run(async () => {
      if (grade !== (profile?.grade ?? "")) {
        const { data } = await api.patch("/profile", { grade });
        setProfile(data);
        toast.success(grade ? `Level set to grade ${grade}` : "We'll start from foundations");
      }
      const res = await api.post("/curriculum/generate", {
        duration_months: Number(form.duration_months),
        depth: form.depth,
        focus_note: form.focus_note,
      });
      toast.info("The advisor is drafting — you can edit everything it suggests.");
      return res;
    });

  const activate = () => run(() => api.post("/curriculum/activate", { curriculum_id: selectedId }));
  const addSubject = (name) => run(() => api.post(`/curriculum/${selectedId}/subjects`, { name }));
  const removeSubject = (subject_id) => run(() => api.delete(`/curriculum/${selectedId}/subjects/${subject_id}`));
  const addChapter = (subject_id, name) => run(() => api.post(`/curriculum/${selectedId}/chapters`, { subject_id, name }));
  const removeChapter = (chapter_id) => run(() => api.delete(`/curriculum/${selectedId}/chapters/${chapter_id}`));

  const input = (key) => draftInput[key] || "";
  const setInput = (key, value) => setDraftInput({ ...draftInput, [key]: value });

  if (!list) return <Shell><Loader label="Loading curriculum…" /></Shell>;

  return (
    <Shell>
      <div className="reveal">
        <p className="rule-label">Curriculum Advisor Agent · your plan, your call</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold">Your curriculum</h1>
        <p className="mt-2 text-sm text-slate-500 max-w-2xl">
          Subject → Chapter → Topic. One topic is one study activity. Subjects and chapters are yours — add what you
          want, drop what you don't — and the advisor writes the topics inside them in the right order.
        </p>

        <div className="mt-6">
          <Guide>
            {list.curricula.length
              ? "Draft a new version any time. The plan you're studying from keeps running until you start the new one."
              : "Three quick questions and the advisor will draft your curriculum."}
          </Guide>
        </div>

        <div className="mt-6 card-flat p-6 space-y-5" data-testid="curriculum-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="rule-label">How long is this plan?</label>
              <select data-testid="curriculum-months" className={`${field} mt-2`} value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })}>
                {[1, 2, 3, 4, 6, 9, 12].map((m) => (
                  <option key={m} value={m}>{m} {m === 1 ? "month" : "months"}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="rule-label">Anything to include or leave out?</label>
              <input
                data-testid="curriculum-focus"
                className={`${field} mt-2`}
                value={form.focus_note}
                onChange={(e) => setForm({ ...form, focus_note: e.target.value })}
                placeholder="e.g. must include coding, go easy on chemistry"
              />
            </div>
          </div>
          <div>
            <label className="rule-label">How deep?</label>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {DEPTHS.map(([key, label, hint]) => (
                <button
                  key={key}
                  data-testid={`curriculum-depth-${key}`}
                  onClick={() => setForm({ ...form, depth: key })}
                  className={`text-left p-4 rounded-2xl border transition-colors ${
                    form.depth === key ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <p className="font-semibold text-sm">{label}</p>
                  <p className="mt-1 text-xs text-slate-500">{hint}</p>
                </button>
              ))}
            </div>
          </div>
          {profile && (
            <div data-testid="curriculum-grade-block">
              <label className="rule-label">Confirm your level</label>
              <div className="mt-3">
                <GradeBox
                  testId="curriculum-grade-box"
                  grade={grade ?? ""}
                  recommended={profile.recommended_grade}
                  age={profile.age}
                  onChange={setGrade}
                  note="This sets the level the advisor targets. You can change it any time in your profile."
                />
              </div>
            </div>
          )}
          <button data-testid="curriculum-generate" onClick={generate} disabled={busy || drafting || !profile} className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
            <Sparkles className="w-4 h-4" />
            {list.curricula.length ? "Draft new version" : "Design my curriculum"}
            {grade ? ` · Grade ${grade}` : ""}
          </button>
        </div>

        {drafting && (
          <div className="mt-6">
            <Loader label="Reasoning before recommending…" note="Building subjects, chapters and topics from your goal and interests. About a minute." />
          </div>
        )}

        {!list.curricula.length && !drafting && (
          <div className="mt-8">
            <Empty title="No curriculum yet" note="Answer the three questions above, then reshape whatever comes back — all of it is yours to change." />
          </div>
        )}

        {list.curricula.length > 0 && (
          <div className="mt-8 grid lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3 space-y-2" data-testid="curriculum-versions">
              <p className="rule-label">Versions</p>
              {list.curricula.map((c) => (
                <button
                  key={c.curriculum_id}
                  data-testid={`curriculum-version-${c.version}`}
                  onClick={() => setSelectedId(c.curriculum_id)}
                  className={`w-full text-left card-flat p-4 ${selectedId === c.curriculum_id ? "!border-slate-900" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-display font-semibold text-sm">v{c.version}</span>
                    <Pill tone={statusTone[c.status] || "slate"}>{c.status}</Pill>
                    {c.grade ? <Pill tone="slate">grade {c.grade}</Pill> : null}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{c.label}</p>
                  <p className="mt-0.5 text-xs text-slate-400">{c.duration_months} months</p>
                </button>
              ))}
            </aside>

            <section className="lg:col-span-9 space-y-5" data-testid="curriculum-detail">
              {selected && (
                <div className="card-flat p-6">
                  <div className="flex flex-wrap items-center gap-3">
                    <Pill tone={statusTone[selected.status] || "slate"} testId="curriculum-status">{selected.status}</Pill>
                    <span className="font-display text-lg font-semibold">{selected.label}</span>
                    {detail?.tree && (
                      <Pill tone="slate">
                        {detail.tree.length} subjects · {detail.tree.reduce((n, s) => n + s.chapters.length, 0)} chapters ·{" "}
                        {detail.tree.reduce((n, s) => n + s.chapters.reduce((m, c) => m + c.topics.length, 0), 0)} topics
                      </Pill>
                    )}
                  </div>
                  {selected.rationale && (
                    <div className="mt-4">
                      <Guide testId="curriculum-rationale">{selected.rationale}</Guide>
                    </div>
                  )}
                  {selected.status === "draft" && (
                    <button data-testid="curriculum-activate" onClick={activate} disabled={busy} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
                      <Play className="w-4 h-4" /> Start this plan
                    </button>
                  )}
                  {selected.status === "active" && (
                    <p className="mt-4 text-sm text-slate-500" data-testid="curriculum-active-note">
                      This is the plan you're studying from. Edit it freely — changes go into a new draft version, and
                      this one keeps running until you start that one.
                    </p>
                  )}
                </div>
              )}

              {(detail?.tree || []).map((subject) => (
                <div key={subject.subject_id} className="card-flat p-6" data-testid={`curriculum-subject-${subject.subject_id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-xl font-semibold">{subject.name}</h3>
                    <Pill tone="slate">
                      {subject.chapters.length} chapters · {subject.chapters.reduce((n, c) => n + c.topics.length, 0)} topics
                    </Pill>
                    {editable && (
                      <button
                        data-testid={`subject-remove-${subject.subject_id}`}
                        onClick={() => {
                          if (window.confirm(`Remove “${subject.name}” and all its chapters and topics?`)) removeSubject(subject.subject_id);
                        }}
                        disabled={busy}
                        title="Remove subject"
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove subject
                      </button>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-500">{subject.rationale}</p>

                  <div className="mt-4 space-y-2">
                    {subject.chapters.map((chapter) => {
                      const isOpen = open[chapter.chapter_id] ?? true;
                      return (
                        <div key={chapter.chapter_id} className="border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-3 px-4 py-3">
                            <button onClick={() => setOpen({ ...open, [chapter.chapter_id]: !isOpen })} data-testid={`chapter-toggle-${chapter.chapter_id}`} className="flex items-center gap-2 text-left flex-1">
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                              <span className="font-semibold text-sm">{chapter.name}</span>
                              <span className="text-xs text-slate-400">~{chapter.est_hours} h</span>
                            </button>
                            {editable && (
                              <button
                                data-testid={`chapter-remove-${chapter.chapter_id}`}
                                onClick={() => removeChapter(chapter.chapter_id)}
                                disabled={busy}
                                title="Remove chapter"
                                className="p-1.5 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors disabled:opacity-40"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {isOpen && (
                            <div className="px-4 pb-4">
                              <ul className="divide-y divide-slate-100">
                                {chapter.topics.map((topic) => (
                                  <li key={topic.topic_id} className="py-2.5" data-testid={`topic-row-${topic.topic_id}`}>
                                    <p className="text-sm font-medium">{topic.title}</p>
                                    <p className="mt-0.5 text-sm text-slate-500">{topic.summary}</p>
                                  </li>
                                ))}
                                {!chapter.topics.length && <li className="py-2.5 text-sm text-slate-400">No topics yet.</li>}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {editable && (
                    <div className="mt-4 flex gap-2">
                      <input
                        data-testid={`chapter-add-input-${subject.subject_id}`}
                        value={input(`c${subject.subject_id}`)}
                        onChange={(e) => setInput(`c${subject.subject_id}`, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && input(`c${subject.subject_id}`).trim()) {
                            addChapter(subject.subject_id, input(`c${subject.subject_id}`).trim());
                            setInput(`c${subject.subject_id}`, "");
                          }
                        }}
                        placeholder={`Add a chapter to ${subject.name}…`}
                        className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                      />
                      <button
                        data-testid={`chapter-add-${subject.subject_id}`}
                        onClick={() => {
                          const value = input(`c${subject.subject_id}`).trim();
                          if (!value) return;
                          addChapter(subject.subject_id, value);
                          setInput(`c${subject.subject_id}`, "");
                        }}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> Chapter
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {editable && (
                <div className="card-flat p-6" data-testid="subject-add-block">
                  <p className="rule-label">Add a subject</p>
                  <p className="mt-2 text-sm text-slate-500">
                    Name it in your own words — the advisor builds the chapters and topics inside it at your level.
                  </p>
                  <div className="mt-4 flex gap-2">
                    <input
                      data-testid="subject-add-input"
                      value={input("newSubject")}
                      onChange={(e) => setInput("newSubject", e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && input("newSubject").trim()) {
                          addSubject(input("newSubject").trim());
                          setInput("newSubject", "");
                        }
                      }}
                      placeholder="e.g. Astronomy, Robotics, Environmental Science…"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500"
                    />
                    <button
                      data-testid="subject-add"
                      onClick={() => {
                        const value = input("newSubject").trim();
                        if (!value) return;
                        addSubject(value);
                        setInput("newSubject", "");
                      }}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Subject
                    </button>
                  </div>
                </div>
              )}

              {busy && <p className="text-sm text-slate-500" data-testid="curriculum-busy">Advisor is working on your edit…</p>}
            </section>
          </div>
        )}
      </div>
    </Shell>
  );
}
